import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import Logo from "../components/common/Logo";
import { ROUTES } from "../utils/constants";
import aboutPdf from "../assets/docs/about.pdf";

/* ── Inline styles reused across sections ─────────────────────────── */
const S = {
  // Layout
  section: { padding: "5rem 1.5rem", maxWidth: "1200px", margin: "0 auto" },
  sectionFull: { padding: "5rem 1.5rem" },

  // Typography
  eyebrow: {
    display: "inline-block",
    fontSize: "0.75rem",
    fontWeight: "700",
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "#3b82f6",
    background: "rgba(59,130,246,0.1)",
    padding: "4px 12px",
    borderRadius: "999px",
    marginBottom: "1rem",
  },
  h2: {
    fontSize: "clamp(1.75rem, 4vw, 2.75rem)",
    fontWeight: "800",
    lineHeight: "1.2",
    color: "var(--text-primary)",
    margin: "0 0 1rem",
  },
  lead: {
    fontSize: "1.0625rem",
    color: "var(--text-secondary)",
    lineHeight: "1.75",
    margin: "0 0 2rem",
  },
};

/* ── Service data ──────────────────────────────────────────────────── */
const SERVICES = [
  {
    icon: "📦",
    title: "የቁሳቁስ አቅርቦት",
    en: "Materials Supply",
    desc: "ከአምራቹ ወገን በቀጥታ ጥሬ ዕቃዎችን በማምጣት እና ከውጭ የሚገቡ ምርቶችን ከአቅራቢዎች በመረከብ የትራንሰፖርትና የሰራተኞችን የአገልግሎት ክፍያ በማሰብ ለአባሎቻችን  ከትርፍ ነጻ አገልግሎት መስጠት።",
    color: "#f59e0b",
    bg: "rgba(245,158,11,0.08)",
  },
  {
    icon: "🏗️",
    title: "የመሰረተ ልማት ድጋፍ",
    en: "Infrastructure Support",
    desc: "ከሀገር ዉጭ ለሚኖሩ ኢትዮጵያዊያን የጀመሩትን እና ሊጀምሩ ያሰቡትን ሁሉንም መሰረተ ልማት' ልምድ ባላቸው ባለሙያወች የተለየ ስልጠና መመሪያ እና ስምምነት በማድረግ እንዲጠናቀቁ እና አገልግሎት እንዲሰጡ በማድረግ አብሮ መስራት።",
    color: "#10b981",
    bg: "rgba(16,185,129,0.08)",
  },
  {
    icon: "🏨",
    title: "የእንግዳ ተቀባይነት አገልግሎቶች",
    en: "Hospitality Services",
    desc: "የሆቴል፤ የካፌ እና የትራንስፖርት አገልግሎቶችን ከትርፍ ነጻ በማቅረብ አባሎቻችንን ተጠቃሚ ማድረግ።",
    color: "#8b5cf6",
    bg: "rgba(139,92,246,0.08)",
  },
  {
    icon: "📱",
    title: "ዲጂታል አገልግሎቶች",
    en: "Digital Services",
    desc: "ማንኛውንም የደንበኝነት አገልግሎት በሞባይል አፕሊኬሽን በመጠቀም ትዛዛትን እና ክፍያን በተመለከተ የግል እና የመንግስት ባንኮችን  በመገልገል ከጥሬ ገንዘብ አገልግሎት በመውጣት ሲስተማቲክ በማድረግ አገልግሎት እነሰጣለን።",
    color: "#3b82f6",
    bg: "rgba(59,130,246,0.08)",
  },
  {
    icon: "💳",
    title: "የግብርና ልማት ድጋፍ",
    en: "Agricultural Development Support",
    desc: "ለአምራች ገበሬዎች የግብርና ምርቶችን ውጤታማ ለማድረግ ለአባሎቻችን አስፈላጊ የሆኑ ቁሳቁሶችን ከትርፍ ነጻ በማቅረብ እና ስልጠናወችን በመስጠት ምርታማ ማድረግ።",
    color: "#ef4444",
    bg: "rgba(239,68,68,0.08)",
  },
  {
    icon: "✨",
    title: "እና ሌሎችም...",
    en: "And More",
    desc: "ለደንበኞቻችን የተለያዩ ተጨማሪ አገልግሎቶችን በቀጣይ እናቀርባለን",
    color: "#6b7280",
    bg: "rgba(107,114,128,0.08)",
  },
];

/* ── Bank data ─────────────────────────────────────────────────────── */
const ETH_BANKS = [
  { name: "Bank of Abyssinia", account: "250163851" },
  { name: "Amhara Bank", account: "9900045801105" },
  { name: "Hibret Bank", account: "2310413074297016" },
  { name: "Dashen Bank", account: "0020831334011" },
  { name: "Awash Bank", account: "013041757778800" },
  { name: "Oromia Bank", account: "1932475200001" },
  { name: "Commercial Bank of Ethiopia", account: "1000764196426" },
  { name: "Addis Bank", account: "1311024009104001" },
];

const INTL_BANKS = [
  {
    name: "JANAMORA INTERNATIONAL ORG INC",
    address: "325 WESTON RD, YORK, QN M6N4Z9",
    account: "1987-047",
    swift: "BOFMCAM2",
    bank: "Bank of Montreal",
  },
  {
    name: "Mirtsew Trading PLC",
    address: "FCY Saving Account (USD)",
    account: "9900048801467",
    swift: "AMHRETAA",
    bank: "Amhara Bank",
  },
  {
    name: "Mirtsew Trading PLC",
    address: "USD Account",
    account: "1311024009224001",
    swift: "ABSCETAA",
    bank: "Addis Bank",
  },
];

const Home = () => {
  const navigate = useNavigate();
  const [showPdf, setShowPdf] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [navScrolled, setNavScrolled] = useState(false);
  const blobUrlRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setNavScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    return () => {
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    };
  }, []);

  const openPdf = async () => {
    setShowPdf(true);
    if (blobUrlRef.current) return;
    setPdfLoading(true);
    try {
      const res = await fetch(aboutPdf);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      blobUrlRef.current = url;
      setPdfBlobUrl(url);
    } catch {
      setPdfBlobUrl(aboutPdf);
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div
      style={{
        fontFamily: "system-ui, -apple-system, sans-serif",
        overflowX: "hidden",
      }}
    >
      {/* ── Sticky Nav ── */}
      <nav
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          background: navScrolled ? "rgba(10, 15, 30, 0.88)" : "transparent",
          backdropFilter: navScrolled ? "blur(20px)" : "none",
          borderBottom: navScrolled
            ? "1px solid rgba(255,255,255,0.07)"
            : "none",
          transition: "all 0.3s ease",
          padding: "0.375rem 1.5rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Logo size="medium" />
        <button
          type="button"
          onClick={() => navigate(ROUTES.REGISTER)}
          style={{
            background: "linear-gradient(135deg, #3b82f6, #1d4ed8)",
            color: "#fff",
            border: "none",
            padding: "0.625rem 1.5rem",
            borderRadius: "999px",
            fontWeight: "600",
            fontSize: "0.875rem",
            cursor: "pointer",
            boxShadow: "0 4px 12px rgba(59,130,246,0.35)",
          }}
        >
          Register Now
        </button>
      </nav>

      {/* ── Hero ── */}
      <section
        style={{
          minHeight: "100vh",
          background:
            "linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #0f2744 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          padding: "7rem 1.5rem 4rem",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative blobs */}
        <div
          style={{
            position: "absolute",
            top: "10%",
            left: "5%",
            width: "320px",
            height: "320px",
            background: "rgba(59,130,246,0.15)",
            borderRadius: "50%",
            filter: "blur(80px)",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: "15%",
            right: "5%",
            width: "280px",
            height: "280px",
            background: "rgba(16,185,129,0.12)",
            borderRadius: "50%",
            filter: "blur(80px)",
            pointerEvents: "none",
          }}
        />

        <div style={{ position: "relative", maxWidth: "800px" }}>
          <Logo
            size="large"
            style={{ marginBottom: "2rem", filter: "brightness(0) invert(1)" }}
          />
          <div
            style={{
              ...S.eyebrow,
              color: "#60a5fa",
              background: "rgba(96,165,250,0.15)",
              marginBottom: "1.5rem",
            }}
          >
            🇪🇹 Ethiopian Trading PLC
          </div>
          <h1
            style={{
              fontSize: "clamp(2rem, 6vw, 4rem)",
              fontWeight: "900",
              color: "#fff",
              lineHeight: "1.15",
              margin: "0 0 1rem",
              letterSpacing: "-0.02em",
            }}
          >
            እንኳን ወደ ምርጥ ሰው
            <br />
            <span
              style={{
                background: "linear-gradient(90deg, #60a5fa, #34d399)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              ትሬዲንግ ኃ/የ/ተ/የግ/ማህ
            </span>
          </h1>
          <p
            style={{
              fontSize: "1.25rem",
              color: "rgba(255,255,255,0.75)",
              marginBottom: "2.5rem",
            }}
          >
            Welcome to MirtSew Trading PLC — serving members across Ethiopia and
            the Diaspora
          </p>
          <div
            style={{
              display: "flex",
              gap: "1rem",
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <button
              type="button"
              onClick={() => navigate(ROUTES.REGISTER)}
              style={{
                background: "linear-gradient(135deg, #3b82f6, #1d4ed8)",
                color: "#fff",
                border: "none",
                padding: "0.875rem 2.25rem",
                borderRadius: "999px",
                fontWeight: "700",
                fontSize: "1rem",
                cursor: "pointer",
                boxShadow: "0 8px 24px rgba(59,130,246,0.4)",
              }}
            >
              ይመዝገቡ — Register Now →
            </button>
            <button
              type="button"
              onClick={openPdf}
              style={{
                background: "rgba(255,255,255,0.1)",
                color: "#fff",
                border: "1.5px solid rgba(255,255,255,0.3)",
                padding: "0.875rem 2.25rem",
                borderRadius: "999px",
                fontWeight: "600",
                fontSize: "1rem",
                cursor: "pointer",
                backdropFilter: "blur(8px)",
              }}
            >
              📄 ዓላማችንን ያንብቡ
            </button>
          </div>

          {/* Stats row */}
          <div
            style={{
              display: "flex",
              gap: "2rem",
              justifyContent: "center",
              marginTop: "4rem",
              flexWrap: "wrap",
            }}
          >
            {[
              ["🏢", "2026", "Founded"],
              ["📦", "6+", "Services"],
              ["🌍", "All", "Regions"],
            ].map(([icon, val, label]) => (
              <div key={label} style={{ textAlign: "center" }}>
                <div style={{ fontSize: "1.5rem", marginBottom: "4px" }}>
                  {icon}
                </div>
                <div
                  style={{ fontSize: "2rem", fontWeight: "800", color: "#fff" }}
                >
                  {val}
                </div>
                <div
                  style={{
                    fontSize: "0.8rem",
                    color: "rgba(255,255,255,0.5)",
                    letterSpacing: "0.05em",
                    textTransform: "uppercase",
                  }}
                >
                  {label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── About / Mission ── */}
      <section
        style={{
          background: "var(--bg-primary, #fff)",
          padding: "5rem 1.5rem",
        }}
      >
        <div
          style={{
            maxWidth: "1100px",
            margin: "0 auto",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "4rem",
            alignItems: "center",
          }}
        >
          <div>
            <div style={S.eyebrow}>የእኛ ዓላማ · Our Mission</div>
            <h2 style={S.h2}>
              የምርጥ ሰው ድርጅት
              <br />
              የመመስረቻ ሃሳብ
            </h2>
            <p style={S.lead}>
              ድርጅታችን ለምን ተቋቋመ? ዓላማችን ምንድን ነው? ለደንበኞቻችን ምን ዓይነት አገልግሎት ለመስጠት ቆርጠን
              ተነስተናል?
            </p>
            <button
              type="button"
              onClick={openPdf}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
                background: "linear-gradient(135deg, #3b82f6, #1d4ed8)",
                color: "#fff",
                border: "none",
                padding: "0.75rem 1.75rem",
                borderRadius: "999px",
                fontWeight: "600",
                fontSize: "0.9375rem",
                cursor: "pointer",
                boxShadow: "0 4px 14px rgba(59,130,246,0.35)",
              }}
            >
              📖 ለማንበብ እዚህ ጋር ይጫኑ
            </button>
          </div>
          {/* Visual card */}
          <div
            style={{
              background:
                "linear-gradient(135deg, rgba(59,130,246,0.08), rgba(16,185,129,0.08))",
              border: "1px solid rgba(59,130,246,0.15)",
              borderRadius: "1.5rem",
              padding: "2.5rem",
            }}
          >
            {[
              ["🎯", "ዓላማ", "ከትርፍ ነጻ አገልግሎት ለሁሉም አባላት"],
              ["🤝", "ትብብር", "ከዲያስፖራ እና ከሀገር ውስጥ አባላት ጋር"],
              ["🌱", "ዕድገት", "ቀጣይ ዕድገት እና ልማት"],
            ].map(([icon, title, desc]) => (
              <div
                key={title}
                style={{
                  display: "flex",
                  gap: "1rem",
                  marginBottom: "1.5rem",
                  alignItems: "flex-start",
                }}
              >
                <div style={{ fontSize: "1.75rem", flexShrink: 0 }}>{icon}</div>
                <div>
                  <div
                    style={{
                      fontWeight: "700",
                      color: "var(--text-primary)",
                      marginBottom: "2px",
                    }}
                  >
                    {title}
                  </div>
                  <div
                    style={{
                      fontSize: "0.875rem",
                      color: "var(--text-secondary)",
                    }}
                  >
                    {desc}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Services ── */}
      <section
        style={{
          background: "var(--bg-secondary, #f9fafb)",
          padding: "5rem 1.5rem",
        }}
      >
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "3.5rem" }}>
            <div style={S.eyebrow}>Our Services · የእኛ አገልግሎቶች</div>
            <h2 style={{ ...S.h2, textAlign: "center" }}>
              ለደንበኞቻችን የምናቀርባቸው
              <br />
              አገልግሎቶች
            </h2>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
              gap: "1.5rem",
            }}
          >
            {SERVICES.map((svc) => (
              <div
                key={svc.title}
                style={{
                  background: "var(--bg-primary, #fff)",
                  borderRadius: "1.25rem",
                  padding: "1.75rem",
                  border: "1px solid var(--border-primary, #e5e7eb)",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  cursor: "default",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-4px)";
                  e.currentTarget.style.boxShadow =
                    "0 12px 32px rgba(0,0,0,0.1)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <div
                  style={{
                    width: "52px",
                    height: "52px",
                    borderRadius: "14px",
                    background: svc.bg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "1.5rem",
                    marginBottom: "1.125rem",
                  }}
                >
                  {svc.icon}
                </div>
                <div
                  style={{
                    fontSize: "0.7rem",
                    fontWeight: "700",
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: svc.color,
                    marginBottom: "0.375rem",
                  }}
                >
                  {svc.en}
                </div>
                <h3
                  style={{
                    fontWeight: "700",
                    fontSize: "1.0625rem",
                    color: "var(--text-primary)",
                    margin: "0 0 0.625rem",
                  }}
                >
                  {svc.title}
                </h3>
                <p
                  style={{
                    fontSize: "0.875rem",
                    color: "var(--text-secondary)",
                    lineHeight: "1.7",
                    margin: 0,
                  }}
                >
                  {svc.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Membership Requirements ── */}
      <section
        style={{
          background: "var(--bg-primary, #fff)",
          padding: "5rem 1.5rem",
        }}
      >
        <div style={{ maxWidth: "900px", margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "3rem" }}>
            <div style={S.eyebrow}>Membership · አባልነት</div>
            <h2 style={{ ...S.h2, textAlign: "center" }}>አባልነት መስፈርቶች</h2>
            <p
              style={{
                ...S.lead,
                textAlign: "center",
                maxWidth: "600px",
                margin: "0 auto 2rem",
              }}
            >
              አገልግሎት ለማግኘት ከሚከተሉት ሁኔታዎች አንዱን ማሟላት ያስፈልጋል
            </p>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "1.5rem",
            }}
          >
            {[
              {
                flag: "🇪🇹",
                title: "በኢትዮጵያ ለሚኖሩ",
                label: "Ethiopia Residents",
                fee: "1,000 ብር",
                req: "ሌላ ዲያስፖራ አባል 1 ሰው ወይም 20 ሀገር ውስጥ አባላት",
                color: "#10b981",
              },
              {
                flag: "🌍",
                title: "ከኢትዮጵያ ውጭ",
                label: "Diaspora",
                fee: "$100 USD",
                req: "ሌላ ዲያስፖራ አባል 3 ሰዎች",
                color: "#3b82f6",
              },
            ].map((m) => (
              <div
                key={m.title}
                style={{
                  borderRadius: "1.25rem",
                  border: `2px solid ${m.color}22`,
                  padding: "2rem",
                  background: `${m.color}08`,
                }}
              >
                <div style={{ fontSize: "2.5rem", marginBottom: "0.75rem" }}>
                  {m.flag}
                </div>
                <div
                  style={{
                    fontSize: "0.7rem",
                    fontWeight: "700",
                    color: m.color,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    marginBottom: "0.375rem",
                  }}
                >
                  {m.label}
                </div>
                <h3
                  style={{
                    fontWeight: "700",
                    fontSize: "1.0625rem",
                    color: "var(--text-primary)",
                    margin: "0 0 1rem",
                  }}
                >
                  {m.title}
                </h3>
                <div
                  style={{
                    background: "var(--bg-primary, #fff)",
                    borderRadius: "0.75rem",
                    padding: "1rem",
                    marginBottom: "0.75rem",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.75rem",
                      color: "var(--text-secondary)",
                      marginBottom: "4px",
                    }}
                  >
                    Registration Fee
                  </div>
                  <div
                    style={{
                      fontSize: "1.5rem",
                      fontWeight: "800",
                      color: m.color,
                    }}
                  >
                    {m.fee}
                  </div>
                </div>
                <p
                  style={{
                    fontSize: "0.875rem",
                    color: "var(--text-secondary)",
                    lineHeight: "1.6",
                    margin: 0,
                  }}
                >
                  {m.req}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Payment / Banks ── */}
      <section
        style={{
          background: "var(--bg-secondary, #f9fafb)",
          padding: "5rem 1.5rem",
        }}
      >
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "3rem" }}>
            <div style={S.eyebrow}>Payment · ክፍያ</div>
            <h2 style={{ ...S.h2, textAlign: "center" }}>Bank Accounts</h2>
            <p style={{ ...S.lead, textAlign: "center" }}>
              Company name: <strong>Mirt Sew Trading PLC</strong>
            </p>
          </div>

          {/* Ethiopian banks */}
          <h3
            style={{
              fontWeight: "700",
              color: "var(--text-primary)",
              marginBottom: "1.25rem",
              fontSize: "1rem",
            }}
          >
            🇪🇹 For Payers in Ethiopia
          </h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
              gap: "1rem",
              marginBottom: "3rem",
            }}
          >
            {ETH_BANKS.map((b) => (
              <div
                key={b.account}
                style={{
                  background: "var(--bg-primary, #fff)",
                  borderRadius: "0.875rem",
                  padding: "1.25rem",
                  border: "1px solid var(--border-primary, #e5e7eb)",
                }}
              >
                <div
                  style={{
                    fontSize: "0.75rem",
                    color: "var(--text-secondary)",
                    marginBottom: "4px",
                  }}
                >
                  {b.name}
                </div>
                <div
                  style={{
                    fontFamily: "monospace",
                    fontWeight: "700",
                    color: "var(--text-primary)",
                    fontSize: "0.9rem",
                    letterSpacing: "0.04em",
                  }}
                >
                  {b.account}
                </div>
              </div>
            ))}
          </div>

          {/* International banks */}
          <h3
            style={{
              fontWeight: "700",
              color: "var(--text-primary)",
              marginBottom: "1.25rem",
              fontSize: "1rem",
            }}
          >
            🌍 For International Payments
          </h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
              gap: "1.25rem",
            }}
          >
            {INTL_BANKS.map((b) => (
              <div
                key={b.account}
                style={{
                  background: "var(--bg-primary, #fff)",
                  borderRadius: "1rem",
                  padding: "1.5rem",
                  border: "1px solid var(--border-primary, #e5e7eb)",
                }}
              >
                <div
                  style={{
                    fontWeight: "700",
                    color: "var(--text-primary)",
                    marginBottom: "0.5rem",
                  }}
                >
                  {b.name}
                </div>
                {b.address && (
                  <div
                    style={{
                      fontSize: "0.8rem",
                      color: "var(--text-secondary)",
                      marginBottom: "0.5rem",
                    }}
                  >
                    {b.address}
                  </div>
                )}
                <div
                  style={{
                    display: "flex",
                    gap: "0.5rem",
                    flexWrap: "wrap",
                    marginTop: "0.5rem",
                  }}
                >
                  <span
                    style={{
                      background: "rgba(59,130,246,0.08)",
                      color: "#3b82f6",
                      padding: "3px 10px",
                      borderRadius: "999px",
                      fontSize: "0.75rem",
                      fontWeight: "600",
                    }}
                  >
                    ACC: {b.account}
                  </span>
                  {b.swift && (
                    <span
                      style={{
                        background: "rgba(16,185,129,0.08)",
                        color: "#10b981",
                        padding: "3px 10px",
                        borderRadius: "999px",
                        fontSize: "0.75rem",
                        fontWeight: "600",
                      }}
                    >
                      SWIFT: {b.swift}
                    </span>
                  )}
                  <span
                    style={{
                      background: "rgba(107,114,128,0.08)",
                      color: "#6b7280",
                      padding: "3px 10px",
                      borderRadius: "999px",
                      fontSize: "0.75rem",
                    }}
                  >
                    {b.bank}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section
        style={{
          background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)",
          padding: "6rem 1.5rem",
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: "50%",
            transform: "translateX(-50%)",
            width: "600px",
            height: "300px",
            background: "rgba(59,130,246,0.12)",
            borderRadius: "50%",
            filter: "blur(80px)",
            pointerEvents: "none",
          }}
        />
        <div
          style={{ position: "relative", maxWidth: "700px", margin: "0 auto" }}
        >
          <div
            style={{
              ...S.eyebrow,
              color: "#60a5fa",
              background: "rgba(96,165,250,0.15)",
            }}
          >
            ዛሬውኑ ይጀምሩ
          </div>
          <h2
            style={{
              fontSize: "clamp(2rem, 5vw, 3.25rem)",
              fontWeight: "900",
              color: "#fff",
              margin: "0 0 1.25rem",
              lineHeight: "1.15",
            }}
          >
            ዛሬውኑ ይመዝገቡ!
          </h2>
          <p
            style={{
              fontSize: "1.125rem",
              color: "rgba(255,255,255,0.7)",
              marginBottom: "2.5rem",
              lineHeight: "1.7",
            }}
          >
            የምርጥ ሰው ትሬዲንግ አባል ይሁኑ እና ከትርፍ ነጻ የሆኑ አገልግሎቶቻችንን ይጠቀሙ
          </p>
          <button
            type="button"
            onClick={() => navigate(ROUTES.REGISTER)}
            style={{
              background: "linear-gradient(135deg, #3b82f6, #1d4ed8)",
              color: "#fff",
              border: "none",
              padding: "1rem 3rem",
              borderRadius: "999px",
              fontWeight: "700",
              fontSize: "1.0625rem",
              cursor: "pointer",
              boxShadow: "0 8px 30px rgba(59,130,246,0.5)",
            }}
          >
            Register Now — ይመዝገቡ →
          </button>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer
        style={{
          background: "#0a0f1e",
          padding: "2rem 1.5rem",
          textAlign: "center",
          color: "rgba(255,255,255,0.4)",
          fontSize: "0.875rem",
        }}
      >
        © 2026 MirtSew Trading PLC. All rights reserved.
      </footer>

      {/* ── PDF Modal ── */}
      {showPdf &&
        createPortal(
          <div
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 9999,
              background: "rgba(0,0,0,0.8)",
              backdropFilter: "blur(6px)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "1rem",
            }}
            onClick={() => setShowPdf(false)}
          >
            <div
              style={{
                background: "var(--bg-primary, #fff)",
                borderRadius: "1rem",
                width: "100%",
                maxWidth: "900px",
                height: "90vh",
                display: "flex",
                flexDirection: "column",
                boxShadow: "0 24px 80px rgba(0,0,0,0.6)",
                overflow: "hidden",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  background: "linear-gradient(135deg, #1e3a5f, #1d4ed8)",
                  padding: "1rem 1.5rem",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexShrink: 0,
                }}
              >
                <h3
                  style={{
                    color: "#fff",
                    margin: 0,
                    fontSize: "0.9375rem",
                    fontWeight: "600",
                  }}
                >
                  📄 የምርጥ ሰው ድርጅት የመመስረቻ መነሻ ሃሳብና ዓላማ
                </h3>
                <div
                  style={{
                    display: "flex",
                    gap: "0.75rem",
                    alignItems: "center",
                  }}
                >
                  <a
                    href={aboutPdf}
                    download="MirtSew-About.pdf"
                    style={{
                      background: "rgba(255,255,255,0.2)",
                      color: "#fff",
                      textDecoration: "none",
                      padding: "6px 14px",
                      borderRadius: "6px",
                      fontSize: "0.8125rem",
                      fontWeight: "500",
                    }}
                  >
                    ⬇️ አውርድ
                  </a>
                  <button
                    onClick={() => setShowPdf(false)}
                    type="button"
                    style={{
                      background: "rgba(255,255,255,0.2)",
                      border: "none",
                      color: "#fff",
                      borderRadius: "50%",
                      width: "32px",
                      height: "32px",
                      cursor: "pointer",
                      fontSize: "1.125rem",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    ×
                  </button>
                </div>
              </div>
              {pdfLoading ? (
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#555",
                    fontSize: "1rem",
                  }}
                >
                  Loading PDF...
                </div>
              ) : (
                <iframe
                  src={pdfBlobUrl || ""}
                  title="About MirtSew"
                  style={{
                    flex: 1,
                    border: "none",
                    width: "100%",
                    background: "#fff",
                  }}
                />
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default Home;
